import { type SyntheticEvent, useEffect, useId, useState } from 'react';
import {
  type PropertySharingFieldPart,
  PropertySharingIdentifierPart,
  PropertySharingTextPart,
  PropertySharingExactNamespace,
  PropertySharingAnyNamespace,
} from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { ExpandableSection } from 'src/modules/shared/components/molecules/expandable-section';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { PropertyLinkPartBindingProps } from '../../../types/property-links.types';
import {
  decodePropertyLinkText,
  encodePropertyLinkText,
  propertyLinkParameterKey,
  propertyLinkSupportsValueMappings,
} from '../../../utils/property-link-template.util';

import { PropertyLinkValueMappings } from './property-link-value-mappings';
import { PropertyLinkParameterSelect } from './property-link-parameter-select';

/** Bind a selected occurrence explicitly; neither URL text nor example data chooses its source. */
export function PropertyLinkPartBinding({
  formId,
  part,
  identifiers,
  disabled,
  onApply,
  mappings,
  encoded,
  onDirtyChange,
  onValidityChange,
}: PropertyLinkPartBindingProps) {
  const { t } = useI18n();
  const [parameter, setParameter] = useState<
    PropertySharingFieldPart | PropertySharingIdentifierPart | null
  >(part.kind === PropertySharingTextPart.kind.TEXT ? null : part);
  const [range, setRange] = useState({
    start: 0,
    end:
      part.kind === PropertySharingTextPart.kind.TEXT ? part.value.length : 0,
  });
  const [selectingSubstring, setSelectingSubstring] = useState(false);
  const modeId = useId();
  const fixedTextId = useId();
  const [mode, setMode] = useState<'variable' | 'fixed'>('variable');
  const textValue =
    part.kind === PropertySharingTextPart.kind.TEXT ? part.value : '';
  const initialText = encoded ? decodePropertyLinkText(textValue) : textValue;
  const [fixedText, setFixedText] = useState(initialText);
  const initialValues =
    mappings.find(
      (item) =>
        part.kind !== PropertySharingTextPart.kind.TEXT &&
        propertyLinkParameterKey(item.parameter) ===
          propertyLinkParameterKey(part)
    )?.values ?? {};
  const [rows, setRows] = useState(
    Object.entries(initialValues).map(([from, to]) => ({ from, to }))
  );
  const supportsMappings = propertyLinkSupportsValueMappings(parameter);
  const mappingRows = supportsMappings ? rows : [];
  const validMappings =
    mappingRows.every((row) => row.from && row.to) &&
    new Set(mappingRows.map((row) => row.from)).size === mappingRows.length;
  const valid =
    !disabled &&
    (mode === 'variable'
      ? parameter !== null && validMappings
      : Boolean(fixedText.trim()));
  useEffect(() => onValidityChange(valid), [valid, onValidityChange]);
  const dirty =
    mode !== 'variable' ||
    fixedText !== initialText ||
    JSON.stringify(parameter) !==
      JSON.stringify(
        part.kind === PropertySharingTextPart.kind.TEXT ? null : part
      ) ||
    JSON.stringify(rows) !==
      JSON.stringify(
        Object.entries(initialValues).map(([from, to]) => ({ from, to }))
      );
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  const apply = () => {
    if (!valid) return;
    const replacement =
      mode === 'fixed'
        ? {
            kind: PropertySharingTextPart.kind.TEXT,
            value:
              fixedText === initialText &&
              part.kind === PropertySharingTextPart.kind.TEXT
                ? part.value
                : encoded
                  ? encodePropertyLinkText(fixedText)
                  : fixedText,
          }
        : parameter;
    if (!replacement) return;
    const mapping =
      mode === 'variable' && parameter
        ? {
            parameter,
            values: Object.fromEntries(
              mappingRows.map(({ from, to }) => [from, to])
            ),
          }
        : undefined;
    if (mode === 'fixed' || part.kind !== PropertySharingTextPart.kind.TEXT) {
      onApply([replacement], mapping);
      return;
    }
    onApply(
      [
        ...(range.start
          ? [
              {
                kind: PropertySharingTextPart.kind.TEXT,
                value: part.value.slice(0, range.start),
              },
            ]
          : []),
        replacement,
        ...(range.end < part.value.length
          ? [
              {
                kind: PropertySharingTextPart.kind.TEXT,
                value: part.value.slice(range.end),
              },
            ]
          : []),
      ],
      mapping
    );
  };
  const toggleSubstring = () => {
    if (selectingSubstring && part.kind === PropertySharingTextPart.kind.TEXT) {
      setRange({ start: 0, end: part.value.length });
    }
    setSelectingSubstring(!selectingSubstring);
  };
  const selectSubstring = (event: SyntheticEvent<HTMLInputElement>) => {
    const { selectionStart, selectionEnd } = event.currentTarget;
    if (
      selectionStart !== null &&
      selectionEnd !== null &&
      selectionEnd > selectionStart
    ) {
      setRange({ start: selectionStart, end: selectionEnd });
    }
  };
  return (
    <form
      id={formId}
      aria-label={t('propertyLinks.editPart')}
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        apply();
      }}
    >
      <fieldset className="space-y-2" disabled={disabled}>
        <Text as="legend" size="small" weight="medium">
          {t('propertyLinks.partBehavior')}
        </Text>
        <div className="grid gap-2 sm:grid-cols-2">
          {(['variable', 'fixed'] as const).map((value) => (
            <label
              key={value}
              htmlFor={`${modeId}-${value}`}
              aria-label={t(
                value === 'variable'
                  ? 'propertyLinks.variableMode'
                  : 'propertyLinks.fixedMode'
              )}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors focus-within:ring-2 focus-within:ring-primary-500 ${mode === value ? 'border-primary-500 bg-primary-50' : 'border-secondary-200 bg-white hover:border-secondary-400'} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              <input
                id={`${modeId}-${value}`}
                type="radio"
                name={modeId}
                value={value}
                checked={mode === value}
                onChange={() => setMode(value)}
                className="mt-1 accent-primary-500"
              />
              <div className="space-y-1">
                <Text as="span" size="small" weight="medium">
                  {t(
                    value === 'variable'
                      ? 'propertyLinks.variableMode'
                      : 'propertyLinks.fixedMode'
                  )}
                </Text>
                <Text size="tiny" color="secondary">
                  {t(
                    value === 'variable'
                      ? 'propertyLinks.variableModeHelp'
                      : 'propertyLinks.fixedModeHelp'
                  )}
                </Text>
              </div>
            </label>
          ))}
        </div>
      </fieldset>
      {mode === 'fixed' ? (
        <div className="space-y-2">
          <label htmlFor={fixedTextId} className="block text-sm font-medium">
            {t('propertyLinks.fixedText')}
          </label>
          <TextInput
            id={fixedTextId}
            value={fixedText}
            disabled={disabled}
            maxLength={2048}
            placeholder={t('propertyLinks.fixedTextPlaceholder')}
            aria-describedby={`${fixedTextId}-help`}
            onChange={(event) => setFixedText(event.target.value)}
          />
          <div id={`${fixedTextId}-help`}>
            <Text size="small" color="secondary">
              {t('propertyLinks.fixedTextHelp')}
            </Text>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div
            className={`grid items-start gap-3 ${part.kind === PropertySharingTextPart.kind.TEXT ? 'sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]' : ''}`}
          >
            {part.kind === PropertySharingTextPart.kind.TEXT && (
              <div className="min-w-0 space-y-2">
                <Text size="small" weight="medium">
                  {t('propertyLinks.selectedText')}
                </Text>
                <div className="rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2">
                  <Text size="small" className="break-all" disableAutoLink>
                    {part.value.slice(range.start, range.end)}
                  </Text>
                </div>
                <Button
                  type="button"
                  size="sm"
                  appearance="ghost"
                  variant="neutral"
                  disabled={disabled}
                  aria-expanded={selectingSubstring}
                  onClick={toggleSubstring}
                >
                  {t(
                    selectingSubstring
                      ? 'propertyLinks.useWholeText'
                      : 'propertyLinks.selectSubstring'
                  )}
                </Button>
              </div>
            )}
            <div className="min-w-0 space-y-2">
              <Text size="small" weight="medium">
                {t('propertyLinks.variableMode')}
              </Text>
              <PropertyLinkParameterSelect
                parameter={parameter}
                identifiers={identifiers}
                disabled={disabled}
                onChange={(next) => {
                  setParameter(next);
                  const values = next
                    ? mappings.find(
                        (item) =>
                          propertyLinkParameterKey(item.parameter) ===
                          propertyLinkParameterKey(next)
                      )?.values ?? {}
                    : {};
                  setRows(
                    Object.entries(values).map(([from, to]) => ({ from, to }))
                  );
                }}
              />
            </div>
            {selectingSubstring &&
              part.kind === PropertySharingTextPart.kind.TEXT && (
                <div className="space-y-2 sm:col-span-2">
                  <Text size="tiny" color="secondary">
                    {t('propertyLinks.selectSubstringHelp')}
                  </Text>
                  <TextInput
                    readOnly
                    disabled={disabled}
                    aria-label={t('propertyLinks.selectSubstring')}
                    value={part.value}
                    onSelect={selectSubstring}
                  />
                </div>
              )}
          </div>
          {parameter?.kind ===
            PropertySharingIdentifierPart.kind.IDENTIFIER && (
            <ExpandableSection
              title={t('propertyLinks.identifierOptions')}
              isInitiallyExpanded={false}
              isContentScrollable={false}
              contentClassName=""
              showScrollHint={false}
            >
              <Select
                aria-label={t('propertyLinks.identifierValue')}
                value={parameter.field}
                disabled={disabled}
                options={[
                  {
                    value: PropertySharingIdentifierPart.field.VALUE,
                    content: t('propertyLinks.rawValue'),
                  },
                  {
                    value: PropertySharingIdentifierPart.field.NORMALIZED_VALUE,
                    content: t('propertyLinks.normalizedValue'),
                  },
                ]}
                onChange={(event) =>
                  setParameter({
                    ...parameter,
                    field:
                      event.target.value === 'normalizedValue'
                        ? PropertySharingIdentifierPart.field.NORMALIZED_VALUE
                        : PropertySharingIdentifierPart.field.VALUE,
                  })
                }
              />
              <Select
                aria-label={t('propertyLinks.namespace')}
                value={parameter.selector.namespace.mode}
                disabled={disabled}
                options={[
                  { value: 'any', content: t('propertyLinks.anyNamespace') },
                  {
                    value: 'exact',
                    content: t('propertyLinks.exactNamespace'),
                  },
                ]}
                onChange={(event) =>
                  setParameter({
                    ...parameter,
                    selector: {
                      ...parameter.selector,
                      namespace:
                        event.target.value === 'exact'
                          ? {
                              mode: PropertySharingExactNamespace.mode.EXACT,
                              value: null,
                            }
                          : { mode: PropertySharingAnyNamespace.mode.ANY },
                    },
                  })
                }
              />
              {parameter.selector.namespace.mode ===
                PropertySharingExactNamespace.mode.EXACT && (
                <TextInput
                  aria-label={t('propertyLinks.namespace')}
                  placeholder={t('propertyLinks.nullNamespace')}
                  disabled={disabled}
                  value={parameter.selector.namespace.value ?? ''}
                  onChange={(event) =>
                    setParameter({
                      ...parameter,
                      selector: {
                        ...parameter.selector,
                        namespace: {
                          mode: PropertySharingExactNamespace.mode.EXACT,
                          value: event.target.value || null,
                        },
                      },
                    })
                  }
                />
              )}
            </ExpandableSection>
          )}
          {parameter && supportsMappings && (
            <PropertyLinkValueMappings
              parameter={parameter}
              rows={rows}
              disabled={disabled}
              onChange={setRows}
            />
          )}
          {!validMappings && (
            <Text size="small" color="warning">
              {t('propertyLinks.incompleteMappings')}
            </Text>
          )}
        </div>
      )}
    </form>
  );
}
